// User Class
// -----------
// Mirrors the back end or graph call
// Supposed to be a simple storage container
//

import DefaultAvatar from "@/assets/images/default_avatar.png";
import { getGraphData } from "@/utils/transform";
import { API, graphqlOperation as _graphqlOperation } from "aws-amplify";
import { assertNoTenantIdInVariables } from "@/services/graphql/assertNoTenantId";

import {
  ADMIN_GROUP,
  BACKEND_CODE_INVALID_DISPLAY_NAME,
  CLIENT_OWNER_GROUP,
  EMPTY_STRING,
  EPOCH_TO_JS_DATE_CONVERSION,
  PENTESTER_GROUP,
} from "../constants/definitions";
import {
  changeDisplayName,
  changeOwner,
  changeUserRole,
  deleteUser,
  resetUserMfa,
} from "../services/graphql/mutations";
import { getUser } from "../services/graphql/queries";

// Outcome of a preferred-username update. Distinct cases because each one needs
// different wording in front of the user.
export type UpdateDisplayNameOutcome = "updated" | "taken" | "invalid" | "error";

export interface UpdateDisplayNameResult {
  outcome: UpdateDisplayNameOutcome;
}

// Force Cognito User Pools auth on every GraphQL call.
const graphqlOperation = (query: any, variables?: any) => {
  // Reject any client-side tenantId leak before the request leaves
  // the wrapper. See src/services/graphql/assertNoTenantId.ts for
  // the contract — symmetric to the BE rule "never accept tenantId
  // from client input."
  assertNoTenantIdInVariables(variables);
  return {
    ..._graphqlOperation(query, variables),
    authMode: "AMAZON_COGNITO_USER_POOLS" as const,
  };
};

interface UserData {
  given_name?: string;
  family_name?: string;
  avatar?: string;
  emailaddress?: string;
  displayName?: string;
  phone?: string;
  role?: string[];
  status?: string;
  lastlogindate?: number;
  currentipaddress?: string;
  previpaddress?: string;
  city?: string;
  country?: string;
  [key: string]: any;
}

interface GraphQLResponse {
  data: any;
  [key: string]: any;
}

class UserClass {
  public given_name: string;
  public family_name: string;
  public avatar: string;
  public emailaddress: string;
  public displayName: string;
  public phone: string;
  public role: string[];
  // Cognito UserStatus. "FORCE_CHANGE_PASSWORD" means the invite was sent
  // but never accepted. Empty when the read path did not supply it.
  public status: string;
  public lastlogindate: Date;
  public currentipaddress: string;
  public previpaddress: string;
  public city: string;
  public country: string;

  constructor() {
    this.given_name = EMPTY_STRING;
    this.family_name = EMPTY_STRING;
    this.avatar = DefaultAvatar;
    this.emailaddress = EMPTY_STRING;
    this.displayName = EMPTY_STRING;
    this.phone = EMPTY_STRING;
    this.role = [];
    this.status = EMPTY_STRING;
    // Advanced user details
    this.lastlogindate = new Date();
    this.currentipaddress = EMPTY_STRING;
    this.previpaddress = EMPTY_STRING;
    this.city = EMPTY_STRING;
    this.country = EMPTY_STRING;
  }

  // Get User
  // ----------
  // No need to pass anything to the call as a null will result in the logged in user's details been returned
  //
  async getUser(): Promise<void> {
    // Make the graph call
    const response = (await API.graphql(
      graphqlOperation(getUser, { id: "" })
    )) as GraphQLResponse;
    const data = getGraphData(response, "getUser");

    if (data) {
      this.setUser(data);
    }

    return data;
  }

  // Set User
  // ---------
  //
  setUser(data: UserData): void {
    // For each setting we check if it exists, else set it to the default
    this.given_name = data.given_name ? data.given_name : EMPTY_STRING;
    this.family_name = data.family_name ? data.family_name : EMPTY_STRING;
    this.avatar = data.avatar && !data.avatar.includes("placeholder-avatar") ? data.avatar : DefaultAvatar;
    this.emailaddress = data.emailaddress ? data.emailaddress : EMPTY_STRING;
    this.displayName = data.displayName ? data.displayName : EMPTY_STRING;
    this.phone = data.phone ? data.phone : EMPTY_STRING;
    this.role = data.role ? data.role : [];
    this.status = data.status ? data.status : EMPTY_STRING;
    // The following will only exist if this is the user calling themselves - which is always true when 'id:""'
    this.lastlogindate = data.lastlogindate
      ? new Date(EPOCH_TO_JS_DATE_CONVERSION * data.lastlogindate)
      : new Date();
    this.currentipaddress = data.currentipaddress
      ? data.currentipaddress
      : EMPTY_STRING;
    this.previpaddress = data.previpaddress ? data.previpaddress : EMPTY_STRING;
    this.city = data.city ? data.city : EMPTY_STRING;
    this.country = data.country ? data.country : EMPTY_STRING;
  }

  // Update Display Name
  // --------------------
  // Sets the caller's own preferred username. Never throws: the caller needs to
  // tell the user WHY it failed, and the three failure modes are different.
  //   * taken           — the name belongs to someone else (pool-wide)
  //   * invalid         — the value canonicalises to nothing usable
  //   * error           — a backend or transport failure
  // On success the local model is updated too, so the page reflects the change
  // without a reload.
  async updateDisplayName(newDisplayName: string): Promise<UpdateDisplayNameResult> {
    try {
      const response = (await API.graphql(
        graphqlOperation(changeDisplayName, {
          input: {
            handle: newDisplayName,
          },
        })
      )) as GraphQLResponse;
      const updateSucceeded = getGraphData(response, "changeDisplayName");

      if (!updateSucceeded) {
        return { outcome: "taken" };
      }

      // Keep the in-memory user in step with what was just persisted.
      this.displayName = newDisplayName;
      return { outcome: "updated" };
    } catch (updateError: any) {
      // Compare against the known sentinel rather than surfacing the raw
      // message: a rejection is attacker-influenceable and may carry backend or
      // transport detail that has no place in the UI or the console.
      const rawMessage =
        updateError?.errors?.[0]?.message || updateError?.message || "";
      if (rawMessage === BACKEND_CODE_INVALID_DISPLAY_NAME) {
        return { outcome: "invalid" };
      }
      console.error("changeDisplayName failed");
      return { outcome: "error" };
    }
  }

  // Identifying a user to the backend
  // ----------------------------------
  // Every mutation that targets ANOTHER user sends `emailaddress`, which is the
  // Cognito Username. It used to send `displayName`, which is the
  // preferred_username — a different value (create_user sets it to the email's
  // local part, so "jane" against a Username of "jane@company.com").
  //
  // Cognito resolves either, because preferred_username is an alias attribute,
  // so both appeared to work. But backend guards that compare against the
  // stored Username saw a value that could never match: the Account Owner
  // guard in change_user_role was silently dead for exactly this reason. The
  // backend now checks resolved users rather than raw strings, and sending the
  // real Username keeps the two layers talking about the same identifier.
  //
  // It also removes a failure mode: a user with no preferred_username sent an
  // empty id, which the backend read as a missing argument and answered with a
  // permission error.

  // Change User Role
  // -----------------
  //
  //
  async changeRole(newRole: string): Promise<boolean> {
    // Validate the role before making the call
    if (PENTESTER_GROUP === newRole || ADMIN_GROUP === newRole) {
      const response = (await API.graphql(
        graphqlOperation(changeUserRole, {
          input: {
            id: this.emailaddress,
            role: newRole,
          },
        })
      )) as GraphQLResponse;
      const data = getGraphData(response, "changeUserRole");

      // If we passed then update the role and return true
      if (data) {
        this.role = [newRole];
        return true;
      }
    }

    return false;
  }

  // Change User Role
  // -----------------
  //
  //
  async changeRoleToClientOwner(): Promise<boolean> {
    const response = (await API.graphql(
      graphqlOperation(changeOwner, { input: this.emailaddress })
    )) as GraphQLResponse;
    const data = getGraphData(response, "changeOwner");
    // If we passed then update the role and return true
    if (data) {
      this.role = [CLIENT_OWNER_GROUP];
      return true;
    }

    return false;
  }

  // Is User Admin
  // --------------
  //
  isUserAdmin(): boolean {
    if (
      this.role.indexOf(CLIENT_OWNER_GROUP) > -1 ||
      this.role.indexOf(ADMIN_GROUP) > -1
    ) {
      return true;
    }
    return false;
  }

  // Is Account Owner — the singleton role allowed to see the Billing
  // surface. For SME tenants every user lands in CLIENT_OWNER_GROUP
  // today; the brief is that when billing arrives this may be tightened
  // to a single per-tenant owner. Keeping the helper isolated here so
  // that tightening is a one-line change rather than a search-and-
  // replace across the app.
  isAccountOwner(): boolean {
    return this.role.indexOf(CLIENT_OWNER_GROUP) > -1;
  }

  // Reset MFA
  // ----------
  // Admin/owner action: clear this user's TOTP factor so they re-enrol at next
  // login (device-loss recovery). The backend rejects resetting the Account
  // Owner or self; those paths are NIMIS support / the self-service profile
  // flow respectively. Returns true on success.
  async resetMfa(): Promise<boolean> {
    const response = (await API.graphql(
      graphqlOperation(resetUserMfa, { input: { id: this.emailaddress } })
    )) as GraphQLResponse;
    const data = getGraphData(response, "resetUserMfa");
    return !!data;
  }

  // Delete User
  // ------------
  // TODO: Delete user is set up in the back end as a list for bulk delete - should we convert?
  //
  async delete(): Promise<boolean> {
    // Delete the user
    const response = (await API.graphql(
      graphqlOperation(deleteUser, { input: { id: [this.emailaddress] } })
    )) as GraphQLResponse;
    const data = getGraphData(response, "deleteUser");
    // If the call succeeded then change the owner locally
    if (data) {
      // Reset this
      this.given_name = EMPTY_STRING;
      this.family_name = EMPTY_STRING;
      this.avatar = DefaultAvatar;
      this.emailaddress = EMPTY_STRING;
      this.displayName = EMPTY_STRING;
      this.phone = EMPTY_STRING;
      this.role = [];
      this.lastlogindate = new Date();
      this.currentipaddress = EMPTY_STRING;
      this.previpaddress = EMPTY_STRING;
      this.city = EMPTY_STRING;
      this.country = EMPTY_STRING;
      return true;
    }

    return false;
  }
}

export default UserClass;
